import type { ReactNode } from 'react';
import { classNames } from './class-names.js';

interface BaseFieldProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
}

interface FieldShellProps extends BaseFieldProps {
  children: ReactNode;
}

/**
 * daisyUI 5 replaced v4's form-control/label-text with fieldset/fieldset-legend/label — but those
 * are visual classes. A real <legend> labels the <fieldset>, not the control inside it, which
 * would leave every field with no accessible name. For a single control the correct element is a
 * <label for>, so that is what this renders, keeping the daisyUI classes for the look.
 * Multi-control groups (see choice-group.tsx) do use a real fieldset/legend.
 */
function FieldShell({ id, label, hint, error, required, children }: FieldShellProps) {
  return (
    <div className="fieldset w-full">
      <label className="fieldset-legend" htmlFor={id}>
        {label}
        <RequiredMark required={required} />
      </label>
      {children}
      <FieldMessage id={`${id}-hint`} hint={hint} error={error} />
    </div>
  );
}

function RequiredMark({ required }: { required?: boolean }) {
  if (!required) {
    return null;
  }

  return (
    <span className="text-error" aria-hidden="true">
      {' *'}
    </span>
  );
}

function FieldMessage({ id, hint, error }: { id: string; hint?: string; error?: string }) {
  if (error) {
    return (
      <p className="label text-error" id={id}>
        {error}
      </p>
    );
  }

  if (!hint) {
    return null;
  }

  return (
    <p className="label" id={id}>
      {hint}
    </p>
  );
}

export interface TextFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: 'text' | 'tel' | 'number' | 'time';
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel';
  maxLength?: number;
  autoComplete?: string;
}

export function TextField(props: TextFieldProps) {
  return (
    <FieldShell {...props}>
      <input
        id={props.id}
        name={props.id}
        type={props.type ?? 'text'}
        className={classNames('input w-full', props.error && 'input-error')}
        value={props.value}
        placeholder={props.placeholder}
        inputMode={props.inputMode}
        maxLength={props.maxLength}
        autoComplete={props.autoComplete}
        aria-invalid={Boolean(props.error)}
        aria-describedby={`${props.id}-hint`}
        onChange={(event) => props.onChange(event.target.value)}
        onBlur={props.onBlur}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export function SelectField(props: SelectFieldProps) {
  return (
    <FieldShell {...props}>
      <select
        id={props.id}
        name={props.id}
        className={classNames('select w-full', props.error && 'select-error')}
        value={props.value}
        aria-invalid={Boolean(props.error)}
        aria-describedby={`${props.id}-hint`}
        onChange={(event) => props.onChange(event.target.value)}
      >
        <option value="">{props.placeholder ?? 'Selecione'}</option>
        {props.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends BaseFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
}

export function TextAreaField(props: TextAreaFieldProps) {
  return (
    <FieldShell {...props}>
      <textarea
        id={props.id}
        name={props.id}
        className={classNames('textarea w-full min-h-24', props.error && 'textarea-error')}
        value={props.value}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        aria-invalid={Boolean(props.error)}
        aria-describedby={`${props.id}-hint`}
        onChange={(event) => props.onChange(event.target.value)}
      />
      <CharCounter value={props.value} maxLength={props.maxLength} />
    </FieldShell>
  );
}

function CharCounter({ value, maxLength }: { value: string; maxLength?: number }) {
  if (!maxLength) {
    return null;
  }

  return (
    <p className="label justify-end tabular-nums" aria-live="polite">
      {value.length}/{maxLength}
    </p>
  );
}
